import apiClient, { type ApiResponse } from './client';

export interface DemoStudioSession {
  id: string;
  organization_id: string;
  created_by_user_id: string;
  title?: string | null;
  status: string;
  browserbase_session_id?: string | null;
  browserbase_region?: string | null;
  live_view_url?: string | null;
  fullscreen_live_view_url?: string | null;
  planned_walkthrough_json?: Record<string, unknown> | null;
  raw_recording_path?: string | null;
  raw_audio_path?: string | null;
  raw_dom_recording_path?: string | null;
  visible_start_ms?: number | null;
  narration_start_ms?: number | null;
  completed_at?: string | null;
  error_message?: string | null;
  organization_video_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DemoStudioAudioUploadResponse {
  audio_url: string;
  session: DemoStudioSession;
}

export interface DemoStudioSessionListResponse {
  sessions: DemoStudioSession[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateDemoStudioSessionRequest {
  organization_id: string;
  title?: string;
  timezone?: string;
  initial_url?: string;
}

class DemoStudioService {
  async createSession(
    payload: CreateDemoStudioSessionRequest,
  ): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>('/api/demo-studio/sessions/', payload);
  }

  async stopSession(sessionId: string): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>(`/api/demo-studio/sessions/${sessionId}/stop`);
  }

  async getSession(sessionId: string): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.get<DemoStudioSession>(`/api/demo-studio/sessions/${sessionId}`);
  }

  async listSessions(
    organizationId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<ApiResponse<DemoStudioSessionListResponse>> {
    return apiClient.get<DemoStudioSessionListResponse>('/api/demo-studio/sessions/', {
      organization_id: organizationId,
      limit: params?.limit ?? 20,
      offset: params?.offset ?? 0,
    });
  }

  async uploadAudio(
    sessionId: string,
    file: File,
  ): Promise<ApiResponse<DemoStudioAudioUploadResponse>> {
    return apiClient.uploadFile<DemoStudioAudioUploadResponse>(
      `/api/demo-studio/sessions/${sessionId}/audio`,
      file,
    );
  }

  async markTimingEvent(
    sessionId: string,
    eventType: 'visible' | 'narration_start',
  ): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>(`/api/demo-studio/sessions/${sessionId}/timing`, {
      event_type: eventType,
    });
  }

  async retryFinalization(sessionId: string): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>(
      `/api/demo-studio/sessions/${sessionId}/retry-finalization`,
    );
  }
}

const demoStudioService = new DemoStudioService();

export default demoStudioService;
